import type { TrackedNewObject } from "@/hooks/trackCreatedObjects";
import { Button } from "@repo/ui/components/Base/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/Base/Dialog";
import { useState } from "react";

const itemTypeKeys: TrackedNewObject["type"][] = [
  "meeting",
  "agenda",
  "agendaModule",
  "agendaItem",
  "document",
];
const itemTypeNames: Record<TrackedNewObject["type"], string> = {
  meeting: "Meetings",
  agenda: "Agendas",
  agendaModule: "Agenda Sections",
  agendaItem: "Agenda Items",
  document: "Documents",
};

export interface ConfirmDeleteOnCancelDialogProps {
  trackedNewObjects: TrackedNewObject[];
  open: boolean;
  setOpen: (open: boolean) => void;
  onReturnToEditing?: () => void | Promise<void>;
  onKeepCreated?: () => void | Promise<void>;
  onConfirmDelete?: () => void | Promise<void>;
  headerClassName?: string;
}

export const ConfirmDeleteOnCancelDialog = ({
  trackedNewObjects,
  open,
  setOpen,
  onReturnToEditing,
  onKeepCreated,
  onConfirmDelete,
  headerClassName,
}: ConfirmDeleteOnCancelDialogProps) => {
  const [itemTypeCounts, itemTypes] = (() => {
    const buckets: Partial<Record<TrackedNewObject["type"], number>> = {};
    for (const trackedNew of trackedNewObjects ?? []) {
      buckets[trackedNew.type] = (buckets[trackedNew.type] ?? 0) + 1;
    }

    const itemTypes = itemTypeKeys
      .map((key) => (buckets[key] ? itemTypeNames[key] : null))
      .filter((v) => v !== null) as string[];

    if (!itemTypes.length) return [buckets, ""];

    if (itemTypes.length > 1) {
      return [
        buckets,
        (
          itemTypes.slice(0, -1).join(", ") +
          ", and " +
          itemTypes[itemTypes.length - 1]
        ).toLowerCase(),
      ];
    }

    return [buckets, itemTypes[0].toLowerCase()];
  })();

  const [disabled, setDisabled] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent>
        <DialogHeader className={headerClassName}>
          <DialogTitle>Confirm delete due to cancel</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p>
            The following {itemTypes} were created during setting up this
            meeting and will be deleted if canceled:
          </p>
          <ul className="my-2">
            {itemTypeKeys.map((key) =>
              itemTypeCounts[key] ? (
                <li key={key}>
                  {itemTypeNames[key]}: {itemTypeCounts[key]}
                </li>
              ) : null
            )}
          </ul>
        </DialogBody>
        <DialogFooter className="justify-between">
          <Button
            type="button"
            disabled={disabled}
            onClick={async () => {
              setDisabled(true);
              await onReturnToEditing?.();
              setDisabled(false);
              setOpen(false);
            }}
          >
            Return to Editing
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={disabled}
            onClick={async () => {
              setDisabled(true);
              await onKeepCreated?.();
              setDisabled(false);
              setOpen(false);
            }}
          >
            Cancel but Keep
          </Button>
          <Button
            type="button"
            variant="destructive"
            disabled={disabled}
            onClick={async () => {
              setDisabled(true);
              await onConfirmDelete?.();
              setDisabled(false);
              setOpen(false);
            }}
          >
            Confirm Deletion
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
